import type { FormatNumberOptions } from 'react-intl';
import { useIntl } from 'react-intl';
import { useFirstMountState } from 'react-use';
import styled, { css, keyframes } from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { useDelayedValue } from 'hooks/state/useDelayedValue';

const Wrapper = styled.span`
  position: relative;
  font-variant-numeric: tabular-nums;
`;

const previousValueAnimation = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const PreviousValue = styled.span`
  transform: translateY(0);
  opacity: 1;
  animation: ${previousValueAnimation} 660ms
    cubic-bezier(0.51, 0.03, 0.29, 0.95) forwards;
`;

const currentValueAnimation = keyframes`
  from {
    transform: translateY(0);
    opacity: 0;
  }
  to {
    transform: translateY(-100%);
    opacity: 1;
  }
`;
const CurrentValue = styled.span`
  transform: translateY(0);
  opacity: 0;
  animation: ${currentValueAnimation} 660ms cubic-bezier(0.51, 0.03, 0.29, 0.95)
    forwards;
`;

const ValueWrapper = styled.span<{ shouldAnimate: boolean }>`
  ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      opacity: 0;
    `}
`;

const AnimationBlockWrapper = styled(Vertical).attrs({ center: true, gap: 0 })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

type Props = {
  value: number;
  formatNumberProps?: FormatNumberOptions;
};

export const SlotMachineAnimatedNumber = ({
  value,
  formatNumberProps,
}: Props) => {
  const { formatNumber } = useIntl();
  const isFirstRender = useFirstMountState();
  const previousValue = useDelayedValue(value, 660);
  const shouldAnimate = !isFirstRender;
  return (
    <Wrapper>
      <ValueWrapper shouldAnimate={shouldAnimate}>
        {formatNumber(value, {
          maximumFractionDigits: 0,
          ...formatNumberProps,
        })}
      </ValueWrapper>
      {!!shouldAnimate && (
        <AnimationBlockWrapper>
          <PreviousValue>
            {formatNumber(previousValue || 0, {
              maximumFractionDigits: 0,
              ...formatNumberProps,
            })}
          </PreviousValue>
          <CurrentValue>
            {formatNumber(value || 0, {
              maximumFractionDigits: 0,
              ...formatNumberProps,
            })}
          </CurrentValue>
        </AnimationBlockWrapper>
      )}
    </Wrapper>
  );
};
